import React, { useEffect, useMemo, useState } from 'react'
import { Card, Table, Badge, Radio, Modal, Select, message, Button, Tag } from 'antd'
import { CommonFilters, UserAvatar, CommonSelector, CommonSelectorProps } from '@components'
import { formatTime, getUrlParam } from '@library'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { TableColumnProps } from '@hooks'
import { useTableService } from '@hooks/useTableService'
import { antdSortMap } from '@configs/map'
import { sortTypeEnum } from '@types'
import { AIQualityCheckPlanListItem, AIQualityCheckPlanListFilter, AIQualityCheckPlanDetailItem } from 'src/types/ai-quality-check'
import { getAIQualityCheckPlanDetail, getAIQualityCheckPlanList, getQualityCheckTaskList } from '@services/ai-quality-check'
import PreviewCompliance from '@pages/config/quality-check-scheme-detail/components/preview-compliance'
import PreviewMoc from '@pages/config/quality-check-scheme-detail/components/preview-moc'
import PreviewScenarioTag from '@pages/config/quality-check-scheme-detail/components/preview-scenario-tag'
import { getSortOrder } from '@configs/table'
import { TableProps } from 'antd/lib/table'
import { QcType, QcTypeMap, QcSchemeStatus, QcSchemeStatusMap, StatusSteps } from '../../../quality-check-scheme-detail/constants'
import { useParams } from 'react-router-dom'

const qcPlanName: CommonSelectorProps<AIQualityCheckPlanListFilter, AIQualityCheckPlanListItem> = {
  id: 'qcPlanName',
  name: '方案名称',
  service: getAIQualityCheckPlanList,
  filter: { type: [QcType.compliance, QcType.moc] },
  valueKey: 'name',
  labelKey: 'name',
  showSearch: true,
  searchKey: 'planName',
  placeholderOnSearching: '按方案名称模糊搜索...',
}

const initFilterValue = {
  status: QcSchemeStatus.optimized,
}

const ChooseQualityCheckPlanPage = ({ onSave, onNextStep, onBack, createId }: {onSave: (data, status?: number) => void, onNextStep: (data) => void, onBack: (data) => void, createId: number }) => {
  const planFilterItems: FieldItem[] = [
    {
      fieldName: 'planName',
      label: '质检方案名称',
      render: () => <CommonSelector {...qcPlanName} />,
    },
    {
      fieldName: 'status',
      label: '调优状态',
      render: () => {
        const options = [
          { label: '待调优', value: QcSchemeStatus.pending },
          { label: '调优中', value: QcSchemeStatus.optimizing },
          { label: '已调优', value: QcSchemeStatus.optimized },
        ]
        return (<Select allowClear options={options} />)
      },
    },
    {
      fieldName: 'updateTime',
      label: '更新时间',
      type: 'rangePicker',
      handleValue: val => ({
        updateTimeFrom: formatTime(val?.[0], 'start'),
        updateTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
  ]

  const initPlanFilter: AIQualityCheckPlanListFilter = {
    page: 1,
    pageSize: 10,
    sort: '',
    sortType: sortTypeEnum.DESC,
    type: [QcType.compliance, QcType.moc],
    status: QcSchemeStatus.optimized,
  }

  const {
    loading: planLoading,
    list: planList,
    filter: planFilter,
    pagination: planPagination,
    setFilter: setPlanFilter,
    resetFilter: resetPlanFilter,
  } = useTableService(getAIQualityCheckPlanList, initPlanFilter, '获取质检方案列表失败')

  const planColumns: TableColumnProps<AIQualityCheckPlanListItem>[] = useMemo(() => [
    {
      title: '方案名称',
      dataIndex: 'name',
      width: 150,
      render: (value, record) => (
        <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="mb5">{value}</div>
            <Button type="dashed" size="small" style={{ borderColor: '#1890ff', color: '#1890ff', width: 80 }}>
              #{QcTypeMap[record?.type]}
            </Button>
          </div>
        </>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 120,
      sorter: true,
      sortOrder: planFilter.sort === 'createTime' ? antdSortMap[planFilter.sortType] : undefined,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 120,
      sorter: true,
      sortOrder: planFilter.sort === 'updateTime' ? antdSortMap[planFilter.sortType] : undefined,
    },
    {
      title: '更新人',
      dataIndex: 'updator',
      width: 100,
      render: value => <UserAvatar salesName={value} />,
    },
    {
      title: '调优状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number, record) => {
        const statusMap = {
          [QcSchemeStatus.pending]: { text: '待调优', color: '#D9D9D9' },
          [QcSchemeStatus.optimizing]: { text: '调优中', color: '#FAAD14' },
          [QcSchemeStatus.optimized]: { text: '已调优', color: '#52C41A' },
        }
        const { text = '待调优', color = '#D9D9D9' } = statusMap[status] || {}
        return <Badge key={record.name} color={color} text={text} />
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (action, record) => (
        <>
          <div
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => {
              getQualityCheckPlanDetail(record)
              setShowPlanDetailModal(true)
            }}
          >
            查看
          </div>
        </>
      ),
    },
  ], [planFilter])

  const initSituationFilter: AIQualityCheckPlanListFilter = {
    page: 1,
    pageSize: 10,
    sort: '',
    sortType: sortTypeEnum.DESC,
    type: [QcType.scenarioTag],
  }

  const {
    loading: situationLoading,
    list: situationList,
    filter: situationFilter,
    pagination: situationPagination,
    setFilter: setSituationFilter,
    resetFilter: resetSituationFilter,
  } = useTableService(getAIQualityCheckPlanList, initSituationFilter, '获取质检情景列表失败')

  const situationColumns: TableColumnProps<AIQualityCheckPlanListItem>[] = useMemo(() => [
    {
      title: '方案名称',
      dataIndex: 'name',
      width: 150,
      render: (value, record) => (
        <>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="mb5">{value}</div>
            <Button type="dashed" size="small" style={{ borderColor: '#1890ff', color: '#1890ff', width: 80 }}>
              #{QcTypeMap[record?.type]}
            </Button>
          </div>
        </>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 120,
      sorter: true,
      sortOrder: situationFilter.sort === 'createTime' ? antdSortMap[situationFilter.sortType] : undefined,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 120,
      sorter: true,
      sortOrder: situationFilter.sort === 'updateTime' ? antdSortMap[situationFilter.sortType] : undefined,
    },
    {
      title: '更新人',
      dataIndex: 'update',
      width: 100,
      render: value => <UserAvatar salesName={value} />,
    },
    {
      title: '调优状态',
      dataIndex: 'status',
      width: 80,
      render: (status: number, record) => {
        const statusMap = {
          [QcSchemeStatus.pending]: { text: '待调优', color: '#D9D9D9' },
          [QcSchemeStatus.optimizing]: { text: '调优中', color: '#FAAD14' },
          [QcSchemeStatus.optimized]: { text: '已调优', color: '#52C41A' },
        }
        const { text = '待调优', color = '#D9D9D9' } = statusMap[status] || {}
        return <Badge key={record.name} color={color} text={text} />
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (action, record) => (
        <>
          <div
            style={{ color: '#1890ff', cursor: 'pointer' }}
            onClick={() => {
              getQualityCheckPlanDetail(record)
              setShowPlanDetailModal(true)
            }}
          >
            查看
          </div>
        </>
      ),
    },
  ], [situationFilter])

  const onPlanTableChange: TableProps<AIQualityCheckPlanListItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<AIQualityCheckPlanListFilter>(sorter)
    setPlanFilter(sort)
  }

  const onSituationTableChange: TableProps<AIQualityCheckPlanListItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<AIQualityCheckPlanListFilter>(sorter)
    setSituationFilter(sort)
  }

  const [situation, setSituation] = useState(true)
  const [showPlanDetailModal, setShowPlanDetailModal] = useState(false)
  const [selectedPlanRowKey, setSelectedPlanRowKey] = useState<React.Key[]>([])
  const [selectedSituationRowKey, setSelectedSituationRowKey] = useState<React.Key[]>([])
  const [PlanDetail, setPlanDetail] = useState<AIQualityCheckPlanDetailItem>()
  const [isStepTwoVaild, setIsStepTwoVaild] = useState(false)
  const [stepTwoData, setStepTwoData] = useState({})
  const taskId = +useParams<{ id: string }>()?.id
  const status = Number(getUrlParam('status'))

  const getQualityCheckTaskCreateData = async () => {
    const params = {
      taskId: taskId || createId,
    }
    const [res, err] = await getQualityCheckTaskList(params)
    if (err) {
      message.error(`获取质检任务数据失败:${err?.message}`)
      return
    }
    setSelectedPlanRowKey(res?.list?.[0]?.planIds?.[0] !== undefined ? [res?.list?.[0]?.planIds?.[0]] : [])
    setSelectedSituationRowKey(res?.list?.[0]?.tagPlanId?.[0] !== undefined ? [res?.list?.[0]?.tagPlanId?.[0]] : [])
    const { planNames, createTime, updateTime, completionRate, total, ...data } = res?.list?.[0] || {}
    setStepTwoData({
      ...data,
      ...(status === StatusSteps.STEP_THREE ? { manualReview: res?.list?.[0]?.manualReview } : {}),
    })
  }

  const getQualityCheckPlanDetail = async (record) => {
    const params = {
      plan: [{ planId: record.id }],
    }
    const [res, err] = await getAIQualityCheckPlanDetail(params)
    if (err) {
      message.error(`获取质检方案详情失败:${err?.message}`)
      return
    }
    const data = res?.list?.find(item => item.id === record.id)
    setPlanDetail(data)
  }

  const handleSituationChange = (e) => {
    setSituation(e.target.value)
  }

  const handleModalCancel = () => {
    setShowPlanDetailModal(false)
  }

  const handlePlanRowSelect = (_, selectedRows) => {
    setSelectedPlanRowKey([selectedRows[0].id])
  }

  const handleSituationRowSelect = (_, selectedRows) => {
    setSelectedSituationRowKey([selectedRows[0].id])
  }

  const showNoPlanWarning = () => {
    if (!isStepTwoVaild) {
      message.warning('未选择质检方案')
      return false
    }
    return true
  }

  const handleBack = () => {
    showNoPlanWarning() && onBack(stepTwoData)
  }

  const handleSaveDraft = () => {
    showNoPlanWarning() && onSave(stepTwoData)
  }

  const handleNextStep = () => {
    showNoPlanWarning() && onNextStep(stepTwoData)
  }

  useEffect(() => {
    if (!isNaN(taskId) || createId) {
      getQualityCheckTaskCreateData()
    }
  }, [taskId, createId])

  useEffect(() => {
    const hasPlanSelected = selectedPlanRowKey.length > 0
    const hasSituationSelected = situation ? selectedSituationRowKey.length > 0 : true
    setIsStepTwoVaild(hasPlanSelected && hasSituationSelected)
    setStepTwoData(prevData => ({
      ...prevData,
      planIds: selectedPlanRowKey,
      tagPlanId: situation ? selectedSituationRowKey : [],
    }))
  }, [selectedPlanRowKey, selectedSituationRowKey, situation])

  return (
    <>
      <Card className="mt20 mb20">
        <CommonFilters
          initValue={initFilterValue}
          className="custom-Label"
          itemList={planFilterItems}
          formLayout={{
            labelCol: { span: 8 },
            wrapperCol: { span: 16 },
          }}
          colProps={{ flex: '1 0 330px' }}
          onSubmit={filter => setPlanFilter({ ...filter, page: 1 })}
          onReset={() => resetPlanFilter(initPlanFilter)}
        />
      </Card>
      <Card className="mt20">
        <Table
          className="mt20"
          loading={planLoading}
          columns={planColumns}
          dataSource={planList}
          pagination={planPagination}
          rowKey={record => record.id}
          rowSelection={{
            type: 'radio',
            onChange: handlePlanRowSelect,
            getCheckboxProps: (record) => ({
              disabled: record.status === QcSchemeStatus.pending || record.status === QcSchemeStatus.optimizing,
            }),
            selectedRowKeys: selectedPlanRowKey,
          }}
          onChange={onPlanTableChange}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 className="mt20 mb20" style={{ fontWeight: '600' }}>情景打标</h4>
          <Radio.Group onChange={handleSituationChange} value={situation}>
            <Radio value>是</Radio>
            <Radio value={false}>否</Radio>
          </Radio.Group>
          {situation && (
            <Table
              className="mt20"
              loading={situationLoading}
              columns={situationColumns}
              dataSource={situationList}
              pagination={situationPagination}
              rowKey={record => record.id}
              rowSelection={{
                type: 'radio',
                onChange: handleSituationRowSelect,
                getCheckboxProps: (record) => ({
                  disabled: record.status === QcSchemeStatus.pending || record.status === QcSchemeStatus.optimizing,
                }),
                selectedRowKeys: selectedSituationRowKey,
              }}
              onChange={onSituationTableChange}
            />
          )}
        </div>
        <div className="mt20 mb20" style={{ display: 'flex' }}>
          <Button type="primary" ghost className="mr10" style={{ color: '#0052D9', borderColor: '#0052D9' }} onClick={handleBack}>上一步</Button>
          <Button type="primary" className="mr10" style={{ backgroundColor: '#0052D9' }} onClick={handleNextStep}>下一步</Button>
          <Button type="primary" className="task-save-btn" onClick={handleSaveDraft}>
            保存草稿
          </Button>
        </div>
      </Card>
      <Modal
        open={showPlanDetailModal}
        width={800}
        footer={null}
        onCancel={handleModalCancel}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex' }}>
            <p>方案名称：</p>
            <p>{PlanDetail?.name}</p>
            <Button type="dashed" size="small" style={{ borderColor: '#1890ff', color: '#1890ff', marginLeft: 20 }}>
              #{QcTypeMap[PlanDetail?.type]}
            </Button>
            <Tag color={QcSchemeStatusMap[PlanDetail?.status as keyof typeof QcSchemeStatusMap]?.color} style={{ marginLeft: 10 }}>{QcSchemeStatusMap[PlanDetail?.status as keyof typeof QcSchemeStatusMap]?.text}</Tag>
          </div>
          {PlanDetail?.type === QcType.compliance && <PreviewCompliance qcPlanDetail={PlanDetail} />}
          {PlanDetail?.type === QcType.moc && <PreviewMoc qcPlanDetail={PlanDetail} />}
          {PlanDetail?.type === QcType.scenarioTag && <PreviewScenarioTag qcPlanDetail={PlanDetail} />}
        </div>
      </Modal>
    </>
  )
}

export default ChooseQualityCheckPlanPage
